import { AxiosObservable } from '@cbridge-utils/axios-observable';
import { omit } from '@cbridge-utils/object';
import { ApiServiceType, BaseService, FILE_UPLOAD_HEADERS, transformBlobResponse, type BlobWithHeaders } from '@cbridge-packages/api-base';
import { IApiGetSchedulesRes } from '../_interfaces/schedule/IApiGetSchedules';
import { IApiExportSchedulesReq } from '../_interfaces/schedule/IApiExportSchedules';
import { IApiCreateScheduleReq, IApiCreateScheduleRes } from '../_interfaces/schedule/IApiCreateSchedule';
import { IApiGetScheduleReq, IApiGetScheduleRes } from '../_interfaces/schedule/IApiGetSchedule';
import { IApiUpdateScheduleReq } from '../_interfaces/schedule/IApiUpdateSchedule';
import { IApiDeleteScheduleReq } from '../_interfaces/schedule/IApiDeleteSchedule';
import { IApiGetScheduleIntervalsReq, IApiGetScheduleIntervalsRes } from '../_interfaces/schedule/IApiGetScheduleIntervals';
import { IApiUpdateScheduleIntervalsReq } from '../_interfaces/schedule/IApiUpdateScheduleIntervals';
import { IApiGetSymbolAssetsReq, IApiGetSymbolAssetsRes } from '../_interfaces/symbols/core/IApiGetSymbolAssets';
import { IApiGetSymbolGroupsReq, IApiGetSymbolGroupsRes } from '../_interfaces/symbols/core/IApiGetSymbolGroups';
import { IApiGetSymbolReq, IApiGetSymbolRes } from '../_interfaces/symbols/core/IApiGetSymbol';
import { IApiGetSymbolsReq, IApiGetSymbolsRes } from '../_interfaces/symbols/core/IApiGetSymbols';
import { IApiExportSymbolsReq } from '../_interfaces/symbols/core/IApiExportSymbols';
import { IApiCreateSymbolReq, IApiCreateSymbolRes } from '../_interfaces/symbols/core/IApiCreateSymbol';
import { IApiUpdateSymbolReq } from '../_interfaces/symbols/core/IApiUpdateSymbol';
import { IApiDeleteSymbolReq } from '../_interfaces/symbols/core/IApiDeleteSymbol';
import { IApiBulkUpdateSymbolReq } from '../_interfaces/symbols/core/IApiBulkUpdateSymbol';
import { IApiBulkDeleteSymbolReq } from '../_interfaces/symbols/core/IApiBulkDeleteSymbol';
import { IApiImportSymbolsFileReq } from '../_interfaces/symbols/core/IApiImportSymbolsFile';
import { IApiGetSyntheticSymbolsReq, IApiGetSyntheticSymbolsRes } from '../_interfaces/symbols/synthetic/IApiGetSyntheticSymbols';
import { IApiGetSyntheticSymbolReq, IApiGetSyntheticSymbolRes } from '../_interfaces/symbols/synthetic/IApiGetSyntheticSymbol';
import { IApiCreateSyntheticSymbolReq, IApiCreateSyntheticSymbolRes } from '../_interfaces/symbols/synthetic/IApiCreateSyntheticSymbol';
import { IApiUpdateSyntheticSymbolReq } from '../_interfaces/symbols/synthetic/IApiUpdateSyntheticSymbol';
import { IApiDeleteSyntheticSymbolReq } from '../_interfaces/symbols/synthetic/IApiDeleteSyntheticSymbol';
import { IApiImportSyntheticSymbolsFileReq } from '../_interfaces/symbols/synthetic/IApiImportSyntheticSymbolsFile';
import { IApiExportSyntheticSymbolsReq } from '../_interfaces/symbols/synthetic/IApiExportSyntheticSymbols';
import { IApiGetLpsRes } from '../_interfaces/lps/IApiGetLps';
import { IApiGetLpReq, IApiGetLpRes } from '../_interfaces/lps/IApiGetLp';
import { IApiUpdateLpReq } from '../_interfaces/lps/IApiUpdateLp';
import { IApiGetLpAdapterSchemaReq, IApiGetLpAdapterSchemaRes } from '../_interfaces/lps/IApiGetLpAdapterSchema';
import { IApiGetLpSymbolReq, IApiGetLpSymbolRes } from '../_interfaces/symbols/lp/IApiGetLpSymbol';
import { IApiGetLpSymbolsReq, IApiGetLpSymbolsRes } from '../_interfaces/symbols/lp/IApiGetLpSymbols';
import { IApiCreateLpSymbolReq } from '../_interfaces/symbols/lp/IApiCreateLpSymbol';
import { IApiUpdateLpSymbolReq } from '../_interfaces/symbols/lp/IApiUpdateLpSymbol';
import { IApiDeleteLpSymbolReq } from '../_interfaces/symbols/lp/IApiDeleteLpSymbol';
import { IApiBulkUpdateLpSymbolReq } from '../_interfaces/symbols/lp/IApiBulkUpdateLpSymbol';
import { IApiBulkDeleteLpSymbolReq } from '../_interfaces/symbols/lp/IApiBulkDeleteLpSymbol';
import { IApiImportLpSymbolsFileReq } from '../_interfaces/symbols/lp/IApiImportLpSymbolsFile';
import { IApiExportLpSymbolsReq } from '../_interfaces/symbols/lp/IApiExportLpSymbols';
import { IApiGetMarkupsReq, IApiGetMarkupsRes } from '../_interfaces/markups/IApiGetMarkups';
import { IApiGetMarkupSymbolReq, IApiGetMarkupSymbolRes } from '../_interfaces/markups/IApiGetMarkupSymbol';
import { IApiUpdateMarkupReq } from '../_interfaces/markups/IApiUpdateMarkup';
import { IApiBulkUpdateMarkupReq } from '../_interfaces/markups/IApiBulkUpdateMarkup';
import { IApiBulkDeleteMarkupReq } from '../_interfaces/markups/IApiBulkDeleteMarkup';
import { IApiImportMarkupsFileReq } from '../_interfaces/markups/IApiImportMarkupsFile';
import { IApiGetMarkupProfilesReq, IApiGetMarkupProfilesRes } from '../_interfaces/markups/IApiGetMarkupProfiles';
import { IApiGetMarkupProfileReq, IApiGetMarkupProfileRes } from '../_interfaces/markups/IApiGetMarkupProfile';
import { IApiCreateMarkupProfileRes, IApiCreateMarkupProfileReq } from '../_interfaces/markups/IApiCreateMarkupProfile';
import { IApiUpdateMarkupProfileReq } from '../_interfaces/markups/IApiUpdateMarkupProfile';
import { IApiDeleteMarkupProfileReq } from '../_interfaces/markups/IApiDeleteMarkupProfile';
import { IApiOrderLifetimeProfilesRes } from '../_interfaces/profiles/orderLifetime/IApiGetOrderLifetimeProfile';
import {
  IApiCreateOrderLifetimeProfileReq, IApiCreateOrderLifetimeProfileRes,
} from '../_interfaces/profiles/orderLifetime/IApiCreateOrderLifetimeProfile';
import { IApiDeleteOrderLifetimeProfileReq } from '../_interfaces/profiles/orderLifetime/IApiDeleteOrderLifetimeProfile';
import { IApiUpdateOrderLifetimeProfileReq } from '../_interfaces/profiles/orderLifetime/IApiUpdateOrderLifetimeProfile';
import { IApiGetNodesReq, IApiGetNodesRes } from '../_interfaces/nodes/IApiGetNodes';
import { IApiCreateNodeRes, IApiCreateNodeReq } from '../_interfaces/nodes/IApiCreateNode';
import { IApiUpdateNodeReq } from '../_interfaces/nodes/IApiUpdateNode';
import { IApiDeleteNodeReq } from '../_interfaces/nodes/IApiDeleteNode';
import { IApiGetStreamsReq, IApiGetStreamsRes } from '../_interfaces/streams/IApiGetStreams';
import { IApiGetStreamReq, IApiGetStreamRes } from '../_interfaces/streams/IApiGetStream';
import { IApiCreateStreamReq, IApiCreateStreamRes } from '../_interfaces/streams/IApiCreateStream';
import { IApiUpdateStreamReq } from '../_interfaces/streams/IApiUpdateStream';
import { IApiDeleteStreamReq } from '../_interfaces/streams/IApiDeleteStream';
import { IApiGetProfileSettingsRes } from '../_interfaces/profile/IApiGetProfileSettings';
import { IApiSaveProfileSettingsReq } from '../_interfaces/profile/IApiSaveProfileSettings';
import { IApiGetUsersReq, IApiGetUsersRes } from '../_interfaces/users/IApiGetUsers';
import { IApiCreateUserReq, IApiCreateUserRes } from '../_interfaces/users/IApiCreateUser';
import { IApiDeleteUserReq } from '../_interfaces/users/IApiDeleteUser';
import { IApiUpdateClientReq } from '../_interfaces/clients/IApiUpdateClient';
import { IApiDeleteClientReq } from '../_interfaces/clients/IApiDeleteClient';
import { IApiGetServersReq, IApiGetServersRes } from '../_interfaces/servers/IApiGetServers';
import { IApiGetServerReq, IApiGetServerRes } from '../_interfaces/servers/IApiGetServer';
import { IApiDeleteServerReq } from '../_interfaces/servers/IApiDeleteServer';
import { IApiCreateServerReq, IApiCreateServerRes } from '../_interfaces/servers/IApiCreateServer';
import { IApiUpdateServerReq } from '../_interfaces/servers/IApiUpdateServer';
import {
  IApiCreateServerStreamReq, IApiUpdateServerStreamReq, IApiDeleteServerStreamReq,
} from '../_interfaces/servers/IApiServerStream';
import { IApiGenerateServerSecretReq, IApiGenerateServerSecretRes } from '../_interfaces/servers/IApiGenerateServerSecret';
import { IApiAdjustmentProfilesRes } from '../_interfaces/profiles/adjustment/IApiGetAdjustmentProfile';
import {
  IApiCreateAdjustmentProfileReq, IApiCreateAdjustmentProfileRes,
} from '../_interfaces/profiles/adjustment/IApiCreateAdjustmentProfile';
import { IApiUpdateAdjustmentProfileReq } from '../_interfaces/profiles/adjustment/IApiUpdateAdjustmentProfile';
import { IApiDeleteAdjustmentProfileReq } from '../_interfaces/profiles/adjustment/IApiDeleteAdjustmentProfile';
import { IApiDeletePriceFilteringProfileReq } from '../_interfaces/profiles/priceFiltering/IApiDeletePriceFilteringProfile';
import {
  IApiPriceFilteringProfilesReq, IApiPriceFilteringProfilesRes,
} from '../_interfaces/profiles/priceFiltering/IApiGetPriceFilteringProfile';
import {
  IApiCreatePriceFilteringProfileReq, IApiCreatePriceFilteringProfileRes,
} from '../_interfaces/profiles/priceFiltering/IApiCreatePriceFilteringProfile';
import { IApiUpdatePriceFilteringProfileReq } from '../_interfaces/profiles/priceFiltering/IApiUpdatePriceFilteringProfile';
import {
  IApiGetServerSymbolsReq, IApiGetNodeSymbolsRes, IApiGetNodeSymbolsReq, IApiGetServerSymbolsRes,
} from '../_interfaces/symbols/client/IApiGetClientSymbols';
import { IApiDeleteClientSymbolReq } from '../_interfaces/symbols/client/IApiDeleteClientSymbol';
import { IApiBulkDeleteClientSymbolReq } from '../_interfaces/symbols/client/IApiBulkDeleteClientSymbol';
import { IApiImportClientSymbolsFileReq } from '../_interfaces/symbols/client/IApiImportClientSymbolsFile';
import { IApiExportNodeClientSymbolsReq } from '../_interfaces/symbols/client/IApiExportNodeClientSymbols';
import { IApiExportServerClientSymbolsReq } from '../_interfaces/symbols/client/IApiExportServerClientSymbols';
import { IApiCreateClientSymbolReq } from '../_interfaces/symbols/client/IApiCreateClientSymbol';
import { IApiUpdateClientSymbolReq } from '../_interfaces/symbols/client/IApiUpdateClientSymbol';
import { IApiPricingProfilesRes } from '../_interfaces/profiles/pricing/IApiGetPricingProfile';
import { IApiRoutingProfilesRes } from '../_interfaces/profiles/routing/IApiGetRoutingProfile';
import { IApiCreateRoutingProfileReq, IApiCreateRoutingProfileRes } from '../_interfaces/profiles/routing/IApiCreateRoutingProfile';
import { IApiUpdateRoutingProfileReq } from '../_interfaces/profiles/routing/IApiUpdateRoutingProfile';
import { IApiDeleteRoutingProfileReq } from '../_interfaces/profiles/routing/IApiDeleteRoutingProfile';
import { IApiUpdatePricingProfileReq } from '../_interfaces/profiles/pricing/IApiUpdatePricingProfile';
import { IApiCreatePricingProfileReq, IApiCreatePricingProfileRes } from '../_interfaces/profiles/pricing/IApiCreatePricingProfile';
import { IApiDeletePricingProfileReq } from '../_interfaces/profiles/pricing/IApiDeletePricingProfile';
import type { IApiCreateSpreadProfileReq, IApiCreateSpreadProfileRes } from '../_interfaces/profiles/spread/IApiCreateSpreadProfile';
import type { IApiDeleteSpreadProfileReq } from '../_interfaces/profiles/spread/IApiDeleteSpreadProfile';
import type { IApiGetSpreadProfileRes } from '../_interfaces/profiles/spread/IApiGetSpreadProfile';
import type { IApiUpdateSpreadProfileReq } from '../_interfaces/profiles/spread/IApiUpdateSpreadProfile';
import { IApiGetAdjustmentRulesRes } from '../_interfaces/rules/adjustment/IApiGetAdjustmentRules';
import { IApiUpdateAdjustmentRulesReq, IApiUpdateAdjustmentRulesRes } from '../_interfaces/rules/adjustment/IApiUpdateAdjustmentRules';
import { IApiImportAdjustmentRulesReq, IApiImportAdjustmentRulesRes } from '../_interfaces/rules/adjustment/IApiImportAdjustmentRules';
import { IApiGetOrderLifetimeRulesRes } from '../_interfaces/rules/orderLifetime/IApiGetOrderLifetimeRules';
import {
  IApiUpdateOrderLifetimeRulesReq, IApiUpdateOrderLifetimeRulesRes,
} from '../_interfaces/rules/orderLifetime/IApiUpdateOrderLifetimeRules';
import { IApiGetPriceFilteringRulesRes } from '../_interfaces/rules/priceFiltering/IApiGetPriceFilteringRules';
import {
  IApiUpdatePriceFilteringRulesReq, IApiUpdatePriceFilteringRulesRes,
} from '../_interfaces/rules/priceFiltering/IApiUpdatePriceFilteringRules';
import { IApiGetPricingRulesRes } from '../_interfaces/rules/pricing/IApiGetPricingRules';
import { IApiUpdatePricingRulesReq, IApiUpdatePricingRulesRes } from '../_interfaces/rules/pricing/IApiUpdatePricingRules';
import {
  IApiImportOrderLifetimeRulesReq, IApiImportOrderLifetimeRulesRes,
} from '../_interfaces/rules/orderLifetime/IApiImportOrderLifetimeRules';
import {
  IApiImportPriceFilteringRulesReq, IApiImportPriceFilteringRulesRes,
} from '../_interfaces/rules/priceFiltering/IApiImportPriceFilteringRules';
import { IApiImportPricingRulesReq, IApiImportPricingRulesRes } from '../_interfaces/rules/pricing/IApiImportPricingRules';
import { IApiGetRoutingRulesRes } from '../_interfaces/rules/routing/IApiGetRoutingRules';
import { IApiUpdateRoutingRulesReq, IApiUpdateRoutingRulesRes } from '../_interfaces/rules/routing/IApiUpdateRoutingRules';
import { IApiGetDictSymbolsParams, IApiGetDictSymbolsRes } from '../_interfaces/dict/IApiGetDictSymbols';
import { IApiGetDictTimezonesRes } from '../_interfaces/dict/IApiGetDictTimezones';
import { IApiImportRoutingRulesReq, IApiImportRoutingRulesRes } from '../_interfaces/rules/routing/IApiImportRoutingRules';
import { IApiGetNodeClientSymbolReq, IApiGetNodeClientSymbolRes } from '../_interfaces/symbols/client/IApiGetNodeClientSymbol';
import { IApiGetServerClientSymbolReq, IApiGetServerClientSymbolRes } from '../_interfaces/symbols/client/IApiGetServerClientSymbol';

export class ClientApiService extends BaseService {
  constructor() {
    super(ApiServiceType.ControlPlane);
  }

  /* ~~~ Schedules ~~~ */

  getScheduleList(): AxiosObservable<IApiGetSchedulesRes> {
    return this.api.get<IApiGetSchedulesRes>('/schedules');
  }

  exportSchedulesFile(payload: IApiExportSchedulesReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/schedules/export', {
      params: payload,
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importSchedules(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/schedules/import', payload, FILE_UPLOAD_HEADERS);
  }

  getScheduleDetails(payload: IApiGetScheduleReq): AxiosObservable<IApiGetScheduleRes> {
    return this.api.get<IApiGetScheduleRes>(`/schedules/${payload.scheduleId}`);
  }

  createSchedule(payload: IApiCreateScheduleReq): AxiosObservable<IApiCreateScheduleRes> {
    return this.api.post<IApiCreateScheduleRes>('/schedules', payload);
  }

  updateSchedule(payload: IApiUpdateScheduleReq): AxiosObservable<void> {
    return this.api.put<void>(`/schedules/${payload.scheduleId}`, omit(payload, 'scheduleId'));
  }

  deleteSchedule(payload: IApiDeleteScheduleReq): AxiosObservable<void> {
    return this.api.delete<void>(`/schedules/${payload.scheduleId}`);
  }

  getScheduleIntervalList(payload: IApiGetScheduleIntervalsReq): AxiosObservable<IApiGetScheduleIntervalsRes> {
    return this.api.get<IApiGetScheduleIntervalsRes>(`/schedules/${payload.scheduleId}/intervals`);
  }

  updateScheduleIntervalList(payload: IApiUpdateScheduleIntervalsReq): AxiosObservable<void> {
    return this.api.put<void>(`/schedules/${payload.scheduleId}/intervals`, omit(payload, 'scheduleId'));
  }

  /* ~~~ Core symbols~~~ */

  getSymbolAssets(payload: IApiGetSymbolAssetsReq): AxiosObservable<IApiGetSymbolAssetsRes> {
    return this.api.get<IApiGetSymbolAssetsRes>('/core/assets', { params: payload });
  }

  getSymbolGroups(payload: IApiGetSymbolGroupsReq): AxiosObservable<IApiGetSymbolGroupsRes> {
    return this.api.get<IApiGetSymbolGroupsRes>('/core/groups', { params: payload });
  }

  getSymbolList(payload: IApiGetSymbolsReq): AxiosObservable<IApiGetSymbolsRes> {
    return this.api.get<IApiGetSymbolsRes>('/core/symbols', { params: payload });
  }

  getSymbolDetails(payload: IApiGetSymbolReq): AxiosObservable<IApiGetSymbolRes> {
    return this.api.get<IApiGetSymbolRes>(`/core/symbols/${payload.symbolId}`);
  }

  createSymbol(payload: IApiCreateSymbolReq): AxiosObservable<IApiCreateSymbolRes> {
    return this.api.post<IApiCreateSymbolRes>('/core/symbols', payload);
  }

  importSymbolsFile(payload: IApiImportSymbolsFileReq): AxiosObservable<void> {
    return this.api.post<void>('/core/import', payload, FILE_UPLOAD_HEADERS);
  }

  exportCoreSymbols(payload: IApiExportSymbolsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/core/export', {
      params: payload,
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  updateSymbol(payload: IApiUpdateSymbolReq): AxiosObservable<void> {
    return this.api.put<void>(`/core/symbols/${payload.symbolId}`, omit(payload, 'symbolId'));
  }

  deleteSymbol(payload: IApiDeleteSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/core/symbols/${payload.symbolId}`);
  }

  bulkUpdateCoreSymbols(payload: IApiBulkUpdateSymbolReq): AxiosObservable<void> {
    return this.api.patch<void>('/core/symbols', payload);
  }

  bulkDeleteCoreSymbols(payload: IApiBulkDeleteSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>('/core/symbols', { data: payload });
  }

  /* ~~~ Synthetic symbols~~~ */

  getSyntheticSymbolList(payload: IApiGetSyntheticSymbolsReq): AxiosObservable<IApiGetSyntheticSymbolsRes> {
    return this.api.get<IApiGetSyntheticSymbolsRes>('/synthetic/symbols', { params: payload });
  }

  getSyntheticSymbolDetails(payload: IApiGetSyntheticSymbolReq): AxiosObservable<IApiGetSyntheticSymbolRes> {
    return this.api.get<IApiGetSyntheticSymbolRes>(`/synthetic/symbols/${payload.symbolId}`);
  }

  createSyntheticSymbol(payload: IApiCreateSyntheticSymbolReq): AxiosObservable<IApiCreateSyntheticSymbolRes> {
    return this.api.post<IApiCreateSyntheticSymbolRes>('/synthetic/symbols', payload);
  }

  updateSyntheticSymbol(payload: IApiUpdateSyntheticSymbolReq): AxiosObservable<void> {
    return this.api.put<void>(`/synthetic/symbols/${payload.symbolId}`, omit(payload, 'symbolId'));
  }

  deleteSyntheticSymbol(payload: IApiDeleteSyntheticSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/synthetic/symbols/${payload.symbolId}`);
  }

  importSyntheticSymbolsFile(payload: IApiImportSyntheticSymbolsFileReq): AxiosObservable<void> {
    return this.api.post<void>('/synthetic/import', payload, FILE_UPLOAD_HEADERS);
  }

  exportSyntheticSymbols(payload: IApiExportSyntheticSymbolsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/synthetic/export', {
      params: payload,
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  /* ~~~ LP symbols~~~ */

  getLpSymbol(payload: IApiGetLpSymbolReq): AxiosObservable<IApiGetLpSymbolRes> {
    return this.api.get<IApiGetLpSymbolRes>(`/lps/${payload.lpId}/symbols/${payload.name}`);
  }

  getLpSymbolList(payload: IApiGetLpSymbolsReq): AxiosObservable<IApiGetLpSymbolsRes> {
    return this.api.get<IApiGetLpSymbolsRes>(`/lps/${payload.lpId}/symbols`, { params: omit(payload, 'lpId') });
  }

  createLpSymbol(payload: IApiCreateLpSymbolReq): AxiosObservable<void> {
    return this.api.post<void>(`/lps/${payload.lpId}/symbols`, omit(payload, 'lpId'));
  }

  importLpSymbolsFile(payload: IApiImportLpSymbolsFileReq): AxiosObservable<void> {
    return this.api.post<void>(`/lps/${payload.lpId}/import`, omit(payload, 'lpId'), FILE_UPLOAD_HEADERS);
  }

  exportLpSymbols(payload: IApiExportLpSymbolsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>(`/lps/${payload.lpId}/export`, {
      params: omit(payload, 'lpId'),
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  updateLpSymbol(payload: IApiUpdateLpSymbolReq): AxiosObservable<void> {
    return this.api.patch<void>(`/lps/${payload.lpId}/symbols/${payload.coreSymbolId}`, omit(payload, 'lpId', 'coreSymbolId'));
  }

  deleteLpSymbol(payload: IApiDeleteLpSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/lps/${payload.lpId}/symbols/${payload.coreSymbolId}`);
  }

  bulkUpdateLpSymbols(payload: IApiBulkUpdateLpSymbolReq): AxiosObservable<void> {
    return this.api.patch<void>(`/lps/${payload.lpId}/symbols`, omit(payload, 'lpId'));
  }

  bulkDeleteLpSymbols(payload: IApiBulkDeleteLpSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/lps/${payload.lpId}/symbols`, { data: omit(payload, 'lpId') });
  }

  /* ~~~ Markups ~~~ */

  getMarkupProfileList(payload: IApiGetMarkupProfilesReq): AxiosObservable<IApiGetMarkupProfilesRes> {
    return this.api.get<IApiGetMarkupProfilesRes>('/markups', { params: payload });
  }

  getMarkupProfile(payload: IApiGetMarkupProfileReq): AxiosObservable<IApiGetMarkupProfileRes> {
    return this.api.get<IApiGetMarkupProfileRes>(`/markups/${payload.profileId}`);
  }

  createMarkupProfile(payload: IApiCreateMarkupProfileReq): AxiosObservable<IApiCreateMarkupProfileRes> {
    return this.api.post<IApiCreateMarkupProfileRes>('/markups', payload);
  }

  updateMarkupProfile(payload: IApiUpdateMarkupProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/markups/${payload.id}`, omit(payload, 'id'));
  }

  deleteMarkupProfile(payload: IApiDeleteMarkupProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/markups/${payload.profileId}`);
  }

  getMarkupList(payload: IApiGetMarkupsReq): AxiosObservable<IApiGetMarkupsRes> {
    return this.api.get<IApiGetMarkupsRes>(`/markups/${payload.profileId}/symbols`, { params: omit(payload, 'profileId') });
  }

  getMarkupSymbol(payload: IApiGetMarkupSymbolReq): AxiosObservable<IApiGetMarkupSymbolRes> {
    return this.api.get<IApiGetMarkupSymbolRes>(`/markups/${payload.profileId}/symbols/${payload.coreSymbolId}`);
  }

  exportMarkupsFile(payload: IApiGetMarkupsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>(`/markups/${payload.profileId}/export`, {
      params: omit(payload, 'profileId'),
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importMarkupsFile(payload: IApiImportMarkupsFileReq): AxiosObservable<void> {
    return this.api.post<void>(`/markups/${payload.profileId}/import`, omit(payload, 'profileId'), FILE_UPLOAD_HEADERS);
  }

  updateMarkup(payload: IApiUpdateMarkupReq): AxiosObservable<void> {
    return this.api.put<void>(`/markups/${payload.profileId}/symbols/${payload.symbolId}`, omit(payload, 'profileId', 'symbolId'));
  }

  bulkUpdateMarkups(payload: IApiBulkUpdateMarkupReq): AxiosObservable<void> {
    return this.api.patch<void>(`/markups/${payload.profileId}/symbols`, omit(payload, 'profileId'));
  }

  bulkDeleteMarkups(payload: IApiBulkDeleteMarkupReq): AxiosObservable<void> {
    return this.api.delete<void>(`/markups/${payload.profileId}/symbols`, { data: omit(payload, 'profileId') });
  }

  /* ~~~ Profiles Order Lifetime ~~~ */

  getOrderLifetimeProfileList(): AxiosObservable<IApiOrderLifetimeProfilesRes> {
    return this.api.get<IApiOrderLifetimeProfilesRes>('/profiles/order-lifetime');
  }

  createOrderLifetimeProfile(payload: IApiCreateOrderLifetimeProfileReq): AxiosObservable<IApiCreateOrderLifetimeProfileRes> {
    return this.api.post<IApiCreateOrderLifetimeProfileRes>('/profiles/order-lifetime', payload);
  }

  updateOrderLifetimeProfile(payload: IApiUpdateOrderLifetimeProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/profiles/order-lifetime/${payload.id}`, omit(payload, 'id'));
  }

  deleteOrderLifetimeProfile(payload: IApiDeleteOrderLifetimeProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/profiles/order-lifetime/${payload.id}`);
  }

  exportOrderLifetimeProfiles(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/profiles/order-lifetime/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importOrderLifetimeProfiles(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/profiles/order-lifetime/import', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Profiles Adjustment ~~~ */

  getAdjustmentProfileList(): AxiosObservable<IApiAdjustmentProfilesRes> {
    return this.api.get<IApiAdjustmentProfilesRes>('/profiles/adjustment');
  }

  createAdjustmentProfile(payload: IApiCreateAdjustmentProfileReq): AxiosObservable<IApiCreateAdjustmentProfileRes> {
    return this.api.post<IApiCreateAdjustmentProfileRes>('/profiles/adjustment', payload);
  }

  updateAdjustmentProfile(payload: IApiUpdateAdjustmentProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/profiles/adjustment/${payload.id}`, omit(payload, 'id'));
  }

  deleteAdjustmentProfile(payload: IApiDeleteAdjustmentProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/profiles/adjustment/${payload.id}`);
  }

  exportAdjustmentProfiles(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/profiles/adjustment/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importAdjustmentProfiles(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/profiles/adjustment/import', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Profiles Pricing ~~~ */

  getPricingProfileList(): AxiosObservable<IApiPricingProfilesRes> {
    return this.api.get<IApiPricingProfilesRes>('/profiles/pricing');
  }

  updatePricingProfile(payload: IApiUpdatePricingProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/profiles/pricing/${payload.id}`, omit(payload, 'id'));
  }

  createPricingProfile(payload: IApiCreatePricingProfileReq): AxiosObservable<IApiCreatePricingProfileRes> {
    return this.api.post<IApiCreatePricingProfileRes>('/profiles/pricing', payload);
  }

  deletePricingProfile(payload: IApiDeletePricingProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/profiles/pricing/${payload.id}`);
  }

  exportPricingProfiles(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/profiles/pricing/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importPricingProfiles(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/profiles/pricing/import', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Profiles Spread ~~~ */

  createSpreadProfile(payload: IApiCreateSpreadProfileReq) {
    return this.api.post<IApiCreateSpreadProfileRes>('/profiles/spreads', payload);
  }

  deleteSpreadProfile(payload: IApiDeleteSpreadProfileReq) {
    return this.api.delete<void>(`/profiles/spreads/${payload.id}`);
  }

  getSpreadProfileList() {
    return this.api.get<IApiGetSpreadProfileRes>('/profiles/spreads');
  }

  updateSpreadProfile(payload: IApiUpdateSpreadProfileReq) {
    return this.api.put<void>(`/profiles/spreads/${payload.id}`, omit(payload, 'id'));
  }

  /* ~~~ Routing profiles ~~~ */

  getRoutingProfileList(): AxiosObservable<IApiRoutingProfilesRes> {
    return this.api.get<IApiRoutingProfilesRes>('/profiles/routing');
  }

  createRoutingProfile(payload: IApiCreateRoutingProfileReq): AxiosObservable<IApiCreateRoutingProfileRes> {
    return this.api.post<IApiCreateRoutingProfileRes>('/profiles/routing', payload);
  }

  updateRoutingProfile(payload: IApiUpdateRoutingProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/profiles/routing/${payload.id}`, omit(payload, 'id'));
  }

  deleteRoutingProfile(payload: IApiDeleteRoutingProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/profiles/routing/${payload.id}`);
  }

  exportRoutingProfiles(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/profiles/routing/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importRoutingProfiles(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/profiles/routing/import', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Profile ~~~ */

  getUserSettings(): AxiosObservable<IApiGetProfileSettingsRes> {
    return this.api.get<IApiGetProfileSettingsRes>('/profile/settings');
  }

  saveUserSettings(payload: IApiSaveProfileSettingsReq): AxiosObservable<void> {
    return this.api.patch<void>('/profile/settings', payload);
  }

  /* ~~~ Users ~~~ */

  getUserList(payload: IApiGetUsersReq): AxiosObservable<IApiGetUsersRes> {
    return this.api.get<IApiGetUsersRes>('/users', { params: payload });
  }

  createUser(payload: IApiCreateUserReq): AxiosObservable<IApiCreateUserRes> {
    return this.api.post<IApiCreateUserRes>('/users', payload);
  }

  deleteUser(payload: IApiDeleteUserReq): AxiosObservable<void> {
    return this.api.delete<void>(`/users/${payload.userId}`);
  }

  /* ~~~ LPs~~~ */

  getLpList(): AxiosObservable<IApiGetLpsRes> {
    return this.api.get<IApiGetLpsRes>('/lps');
  }

  getLp(payload: IApiGetLpReq): AxiosObservable<IApiGetLpRes> {
    return this.api.get<IApiGetLpRes>(`/lps/${payload.lpId}`);
  }

  updateLp(payload: IApiUpdateLpReq): AxiosObservable<void> {
    return this.api.put<void>(`/lps/${payload.lpId}/settings`, omit(payload, 'lpId'));
  }

  getLpAdapterSchema(payload: IApiGetLpAdapterSchemaReq): AxiosObservable<IApiGetLpAdapterSchemaRes> {
    return this.api.get<IApiGetLpAdapterSchemaRes>(`/adapters/${payload.adapterName}`);
  }

  /* ~~~ Nodes ~~~ */

  getNodeList(params?: IApiGetNodesReq): AxiosObservable<IApiGetNodesRes> {
    return this.api.get<IApiGetNodesRes>('/nodes', { params });
  }

  createNode(payload: IApiCreateNodeReq): AxiosObservable<IApiCreateNodeRes> {
    return this.api.post<IApiCreateNodeRes>('/nodes', payload);
  }

  updateNode(payload: IApiUpdateNodeReq): AxiosObservable<void> {
    return this.api.put<void>(`/nodes/${payload.nodeId}`, omit(payload, 'nodeId'));
  }

  deleteNode(payload: IApiDeleteNodeReq): AxiosObservable<void> {
    return this.api.delete<void>(`/nodes/${payload.nodeId}`);
  }

  /* ~~~ Clients ~~~ */

  updateClient(payload: IApiUpdateClientReq): AxiosObservable<void> {
    return this.api.put<void>(`/clients/${payload.clientId}`, omit(payload, 'clientId'));
  }

  deleteClient(payload: IApiDeleteClientReq): AxiosObservable<void> {
    return this.api.delete<void>(`/clients/${payload.clientId}`);
  }

  /* ~~~ Streams ~~~ */

  getStreamList(payload: IApiGetStreamsReq): AxiosObservable<IApiGetStreamsRes> {
    return this.api.get<IApiGetStreamsRes>(`/nodes/${payload.nodeId}/streams`);
  }

  getStream(payload: IApiGetStreamReq): AxiosObservable<IApiGetStreamRes> {
    return this.api.get<IApiGetStreamRes>(`/streams/${payload.streamId}`);
  }

  createStream(payload: IApiCreateStreamReq): AxiosObservable<IApiCreateStreamRes> {
    return this.api.post<IApiCreateStreamRes>(`/nodes/${payload.nodeId}/streams`, omit(payload, 'nodeId'));
  }

  updateStream(payload: IApiUpdateStreamReq): AxiosObservable<void> {
    return this.api.put<void>(`/streams/${payload.streamId}`, omit(payload, 'streamId'));
  }

  deleteStream(payload: IApiDeleteStreamReq): AxiosObservable<void> {
    return this.api.delete<void>(`/streams/${payload.streamId}`);
  }

  /* ~~~ Profiles Price Filtering ~~~ */

  getPriceFilteringProfileList(payload: IApiPriceFilteringProfilesReq): AxiosObservable<IApiPriceFilteringProfilesRes> {
    return this.api.get<IApiPriceFilteringProfilesRes>('/profiles/price-filtering', { params: payload });
  }

  createPriceFilteringProfile(payload: IApiCreatePriceFilteringProfileReq): AxiosObservable<IApiCreatePriceFilteringProfileRes> {
    return this.api.post<IApiCreatePriceFilteringProfileRes>('/profiles/price-filtering', payload);
  }

  updatePriceFilteringProfile(payload: IApiUpdatePriceFilteringProfileReq): AxiosObservable<void> {
    return this.api.put<void>(`/profiles/price-filtering/${payload.id}`, omit(payload, 'id'));
  }

  deletePriceFilteringProfile(payload: IApiDeletePriceFilteringProfileReq): AxiosObservable<void> {
    return this.api.delete<void>(`/profiles/price-filtering/${payload.id}`);
  }

  exportPriceFilteringProfiles(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/profiles/price-filtering/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importPriceFilteringProfiles(payload: { file: File }): AxiosObservable<void> {
    return this.api.post<void>('/profiles/price-filtering/import', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Servers ~~~ */

  getServerList(payload: IApiGetServersReq): AxiosObservable<IApiGetServersRes> {
    return this.api.get<IApiGetServersRes>('/servers', { params: payload });
  }

  getServerDetails(payload: IApiGetServerReq): AxiosObservable<IApiGetServerRes> {
    return this.api.get<IApiGetServerRes>(`/servers/${payload.serverId}`);
  }

  createServer(payload: IApiCreateServerReq): AxiosObservable<IApiCreateServerRes> {
    return this.api.post<IApiCreateServerRes>(`/nodes/${payload.nodeId}/servers`, omit(payload, 'nodeId'));
  }

  updateServer(payload: IApiUpdateServerReq): AxiosObservable<void> {
    return this.api.put<void>(`/servers/${payload.serverId}`, omit(payload, 'serverId'));
  }

  deleteServer(payload: IApiDeleteServerReq): AxiosObservable<void> {
    return this.api.delete<void>(`/servers/${payload.serverId}`);
  }

  generateServerSecret(payload: IApiGenerateServerSecretReq): AxiosObservable<IApiGenerateServerSecretRes> {
    return this.api.put<IApiGenerateServerSecretRes>(`/servers/${payload.serverId}/secret`);
  }

  createServerStream(payload: IApiCreateServerStreamReq): AxiosObservable<void> {
    return this.api.post<void>(`/servers/${payload.serverId}/streams`, omit(payload, 'serverId'));
  }

  updateServerStream(payload: IApiUpdateServerStreamReq): AxiosObservable<void> {
    return this.api.put<void>(`/servers/${payload.serverId}/streams/${payload.streamId}`, omit(payload, 'serverId', 'streamId'));
  }

  deleteServerStream(payload: IApiDeleteServerStreamReq): AxiosObservable<void> {
    return this.api.delete<void>(`/servers/${payload.serverId}/streams/${payload.streamId}`);
  }

  /* ~~~ Client Symbols ~~~ */

  getNodeClientSymbolList(payload: IApiGetNodeSymbolsReq): AxiosObservable<IApiGetNodeSymbolsRes> {
    return this.api.get<IApiGetNodeSymbolsRes>(`/nodes/${payload.nodeId}/symbols`, {
      params: omit(payload, 'nodeId'),
    });
  }

  getServerClientSymbolList(payload: IApiGetServerSymbolsReq): AxiosObservable<IApiGetServerSymbolsRes> {
    return this.api.get<IApiGetServerSymbolsRes>(`/servers/${payload.serverId}/symbols`, {
      params: omit(payload, 'serverId'),
    });
  }

  deleteClientSymbol(payload: IApiDeleteClientSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/nodes/${payload.nodeId}/symbols/${payload.clientSymbolName}`);
  }

  bulkDeleteClientSymbols(payload: IApiBulkDeleteClientSymbolReq): AxiosObservable<void> {
    return this.api.delete<void>(`/nodes/${payload.nodeId}/symbols`, { data: omit(payload, 'nodeId') });
  }

  importClientSymbolsFile(payload: IApiImportClientSymbolsFileReq): AxiosObservable<void> {
    return this.api.post<void>(`/nodes/${payload.nodeId}/import`, omit(payload, 'nodeId'), FILE_UPLOAD_HEADERS);
  }

  exportNodeClientSymbols(payload: IApiExportNodeClientSymbolsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>(`/nodes/${payload.nodeId}/export`, {
      params: omit(payload, 'nodeId'),
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  exportServerClientSymbols(payload: IApiExportServerClientSymbolsReq): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>(`/servers/${payload.serverId}/export`, {
      params: omit(payload, 'serverId'),
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  createClientSymbol(payload: IApiCreateClientSymbolReq): AxiosObservable<void> {
    return this.api.post<void>(`/nodes/${payload.nodeId}/symbols`, omit(payload, 'nodeId'));
  }

  updateClientSymbol(payload: IApiUpdateClientSymbolReq): AxiosObservable<void> {
    return this.api.put<void>(`/nodes/${payload.nodeId}/symbols/${payload.clientSymbolName}`, omit(payload, 'nodeId', 'clientSymbolName'));
  }

  getNodeClientSymbolDetails(payload: IApiGetNodeClientSymbolReq): AxiosObservable<IApiGetNodeClientSymbolRes> {
    const encodedSymbolName = encodeURIComponent(payload.clientSymbolName);
    return this.api.get<IApiGetNodeClientSymbolRes>(`/nodes/${payload.nodeId}/symbols/${encodedSymbolName}`);
  }

  getServerClientSymbolDetails(payload: IApiGetServerClientSymbolReq): AxiosObservable<IApiGetServerClientSymbolRes> {
    const encodedSymbolName = encodeURIComponent(payload.clientSymbolName);
    return this.api.get<IApiGetServerClientSymbolRes>(`/servers/${payload.serverId}/symbols/${encodedSymbolName}`);
  }

  /* ~~~ Adjustment Rules ~~~ */

  getAdjustmentRules(): AxiosObservable<IApiGetAdjustmentRulesRes> {
    return this.api.get<IApiGetAdjustmentRulesRes>('/rules/adjustment');
  }

  updateAdjustmentRules(payload: IApiUpdateAdjustmentRulesReq): AxiosObservable<IApiUpdateAdjustmentRulesRes> {
    return this.api.put<IApiUpdateAdjustmentRulesRes>('/rules/adjustment', payload);
  }

  validateAdjustmentRules(payload: IApiUpdateAdjustmentRulesReq): AxiosObservable<IApiUpdateAdjustmentRulesRes> {
    return this.api.post<IApiUpdateAdjustmentRulesRes>('/rules/adjustment/validate', payload);
  }

  exportAdjustmentRules(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/rules/adjustment/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importAdjustmentRules(payload: IApiImportAdjustmentRulesReq): AxiosObservable<IApiImportAdjustmentRulesRes> {
    return this.api.post<IApiImportAdjustmentRulesRes>('/rules/adjustment/validate-file', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Order Lifetime Rules ~~~ */

  getOrderLifetimeRules(): AxiosObservable<IApiGetOrderLifetimeRulesRes> {
    return this.api.get<IApiGetOrderLifetimeRulesRes>('/rules/order-lifetime');
  }

  updateOrderLifetimeRules(payload: IApiUpdateOrderLifetimeRulesReq): AxiosObservable<IApiUpdateOrderLifetimeRulesRes> {
    return this.api.put<IApiUpdateOrderLifetimeRulesRes>('/rules/order-lifetime', payload);
  }

  validateOrderLifetimeRules(payload: IApiUpdateOrderLifetimeRulesReq): AxiosObservable<IApiUpdateOrderLifetimeRulesRes> {
    return this.api.post<IApiUpdateOrderLifetimeRulesRes>('/rules/order-lifetime/validate', payload);
  }

  exportOrderLifetimeRules(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/rules/order-lifetime/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importOrderLifetimeRules(payload: IApiImportOrderLifetimeRulesReq): AxiosObservable<IApiImportOrderLifetimeRulesRes> {
    return this.api.post<IApiImportOrderLifetimeRulesRes>('/rules/order-lifetime/validate-file', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Price Filtering Rules ~~~ */

  getPriceFilteringRules(): AxiosObservable<IApiGetPriceFilteringRulesRes> {
    return this.api.get<IApiGetPriceFilteringRulesRes>('/rules/price-filtering');
  }

  updatePriceFilteringRules(payload: IApiUpdatePriceFilteringRulesReq): AxiosObservable<IApiUpdatePriceFilteringRulesRes> {
    return this.api.put<IApiUpdatePriceFilteringRulesRes>('/rules/price-filtering', payload);
  }

  validatePriceFilteringRules(payload: IApiUpdatePriceFilteringRulesReq): AxiosObservable<IApiUpdatePriceFilteringRulesRes> {
    return this.api.post<IApiUpdatePriceFilteringRulesRes>('/rules/price-filtering/validate', payload);
  }

  exportPriceFilteringRules(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/rules/price-filtering/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importPriceFilteringRules(payload: IApiImportPriceFilteringRulesReq): AxiosObservable<IApiImportPriceFilteringRulesRes> {
    return this.api.post<IApiImportPriceFilteringRulesRes>('/rules/price-filtering/validate-file', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Pricing Rules ~~~ */

  getPricingRules(): AxiosObservable<IApiGetPricingRulesRes> {
    return this.api.get<IApiGetPricingRulesRes>('/rules/pricing');
  }

  updatePricingRules(payload: IApiUpdatePricingRulesReq): AxiosObservable<IApiUpdatePricingRulesRes> {
    return this.api.put<IApiUpdatePricingRulesRes>('/rules/pricing', payload);
  }

  validatePricingRules(payload: IApiUpdatePricingRulesReq): AxiosObservable<IApiUpdatePricingRulesRes> {
    return this.api.post<IApiUpdatePricingRulesRes>('/rules/pricing/validate', payload);
  }

  exportPricingRules(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/rules/pricing/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importPricingRules(payload: IApiImportPricingRulesReq): AxiosObservable<IApiImportPricingRulesRes> {
    return this.api.post<IApiImportPricingRulesRes>('/rules/pricing/validate-file', payload, FILE_UPLOAD_HEADERS);
  }

  /* ~~~ Routing Rules ~~~ */

  getRoutingRules(): AxiosObservable<IApiGetRoutingRulesRes> {
    return this.api.get<IApiGetRoutingRulesRes>('/rules/routing');
  }

  updateRoutingRules(payload: IApiUpdateRoutingRulesReq): AxiosObservable<IApiUpdateRoutingRulesRes> {
    return this.api.put<IApiUpdateRoutingRulesRes>('/rules/routing', payload);
  }

  getDictSymbols(params?: IApiGetDictSymbolsParams): AxiosObservable<IApiGetDictSymbolsRes> {
    return this.api.get<IApiGetDictSymbolsRes>('/dict/symbols', { params });
  }

  getDictTimezones(): AxiosObservable<IApiGetDictTimezonesRes> {
    return this.api.get<IApiGetDictTimezonesRes>('/dict/timezones');
  }

  validateRoutingRules(payload: IApiUpdateRoutingRulesReq): AxiosObservable<IApiUpdateRoutingRulesRes> {
    return this.api.post<IApiUpdateRoutingRulesRes>('/rules/routing/validate', payload);
  }

  exportRoutingRules(): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>('/rules/routing/export', {
      responseType: 'blob',
      transformResponse: transformBlobResponse,
    });
  }

  importRoutingRules(payload: IApiImportRoutingRulesReq): AxiosObservable<IApiImportRoutingRulesRes> {
    return this.api.post<IApiImportRoutingRulesRes>('/rules/routing/validate-file', payload, FILE_UPLOAD_HEADERS);
  }

}
